/**
 * Resolves all transitive dependencies for a given product using BFS.
 * Handles circular dependencies (e.g., maestro <-> agents) via a visited set.
 * Returns all transitive dependencies, NOT including the product itself.
 */
export function resolveAllDependencies(
  productKey: string,
  dependencyMap: Record<string, string[]>
): Set<string> {
  const visited = new Set<string>();
  const queue: string[] = [...(dependencyMap[productKey] || [])];

  while (queue.length > 0) {
    const dep = queue.shift()!;
    if (visited.has(dep)) continue;
    visited.add(dep);
    const transitiveDeps = dependencyMap[dep] || [];
    for (const td of transitiveDeps) {
      if (!visited.has(td) && td !== productKey) {
        queue.push(td);
      }
    }
  }

  return visited;
}

/**
 * Reverse lookup: for each selectedKey, check if productKey is in
 * resolveAllDependencies(selectedKey). Returns the list of selected
 * product keys that depend (directly or transitively) on productKey.
 */
export function getDependentsOfProduct(
  productKey: string,
  selectedKeys: string[],
  dependencyMap: Record<string, string[]>
): string[] {
  return selectedKeys.filter((selectedKey) => {
    if (selectedKey === productKey) return false;
    const deps = resolveAllDependencies(selectedKey, dependencyMap);
    return deps.has(productKey);
  });
}

/**
 * Like getDependentsOfProduct, but filters out circular relationships.
 * A dependent is "circular" if productKey also transitively depends on it.
 * Only TRUE one-way parents (those that depend on productKey but productKey
 * does NOT depend on them) are returned.
 *
 * Example: maestro depends on agents AND agents depends on maestro.
 * getNonCircularDependents('agents', ['maestro', 'agents'], map)
 * returns [] because maestro is circular with agents.
 *
 * But: action_center depends on orchestrator, orchestrator does NOT depend
 * on action_center. getNonCircularDependents('orchestrator', ['action_center'], map)
 * returns ['action_center'].
 */
export function getNonCircularDependents(
  productKey: string,
  selectedKeys: string[],
  dependencyMap: Record<string, string[]>
): string[] {
  const allDependents = getDependentsOfProduct(productKey, selectedKeys, dependencyMap);
  const productDeps = resolveAllDependencies(productKey, dependencyMap);
  return allDependents.filter((depKey) => !productDeps.has(depKey));
}

/**
 * Given a list of explicitly-selected product keys (roots), compute
 * the full set of products that should be selected (roots + all their
 * transitive dependencies).
 */
export function computeRequiredProducts(
  explicitKeys: string[],
  dependencyMap: Record<string, string[]>
): Set<string> {
  const required = new Set<string>();
  for (const key of explicitKeys) {
    required.add(key);
    resolveAllDependencies(key, dependencyMap).forEach(dep => required.add(dep));
  }
  return required;
}

/**
 * Simple find by key in the product options array.
 */
export function getProductOptionByKey(
  key: string,
  allProducts: ProductOptionInterface[]
): ProductOptionInterface | undefined {
  return allProducts.find((p) => p.key === key);
}
